import { useRef, useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Plus, Shield, Flame, Download, Trash2, FileText, Eye, Share2, Send, Upload, Copy, Mail, CheckCircle2, Clock, Ban, RotateCcw, Archive, ArchiveRestore, FileCheck2 } from 'lucide-react';
import { Switch } from '@/components/ui/switch';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { toast } from 'sonner';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  useContratosMantenimiento,
  type ContratoMantenimiento,
} from '@/hooks/useContratosMantenimiento';
import { ContratoFormDialog } from './ContratoFormDialog';
import { useAuth } from '@/contexts/AuthContext';
import { useSecurityContext } from '@/contexts/MaintenanceContext';
import { generarContratoPdf } from '@/lib/contratos/contratoPdf';
import { CLAUSULA_CRA, TITULO_CLAUSULA_CRA } from '@/lib/contratos/plantillasPorDefecto';
import { getSystemTypeLabel } from '@/lib/systemTypeConstants';
import { supabase } from '@/integrations/supabase/client';

const ESTADO_VARIANT: Record<string, { label: string; variant: any }> = {
  borrador: { label: 'Borrador', variant: 'secondary' },
  pendiente_firma: { label: 'Pendiente de firma', variant: 'outline' },
  vigente: { label: 'Vigente', variant: 'default' },
  vencido: { label: 'Vencido', variant: 'outline' },
  rescindido: { label: 'Rescindido', variant: 'destructive' },
};

function ContratoEstadoTimeline({ c }: { c: ContratoMantenimiento }) {
  const generadoAt = (c as any).created_at as string | undefined;
  const enviadoAt = c.firma_enviado_at;
  const enviado = !!enviadoAt || !!(c as any).token_firma;
  const firmadoAt = c.fecha_firma;
  const firmado = !!firmadoAt;
  const rescindidoAt = c.fecha_rescision;
  const rescindido = c.estado === 'rescindido' || !!rescindidoAt;
  const pasos: Array<{ label: string; done: boolean; date?: string | null; icon: any; tone?: 'danger' }> = [
    { label: 'Generado', done: true, date: generadoAt, icon: FileText },
    { label: enviado ? 'Enviado a firma' : 'Sin enviar a firma', done: enviado, date: enviadoAt, icon: Send },
    {
      label: firmado ? `Firmado${c.firma_origen ? ` (${c.firma_origen})` : ''}` : 'Sin firmar',
      done: firmado,
      date: firmadoAt,
      icon: firmado ? CheckCircle2 : Clock,
    },
  ];
  if (rescindido) {
    pasos.push({ label: 'Rescindido', done: true, date: rescindidoAt, icon: Ban, tone: 'danger' });
  }
  return (
    <div className="flex items-center gap-1.5 flex-wrap mt-2">
      {pasos.map((p, i) => {
        const Icon = p.icon;
        const toneClass = p.done
          ? (p.tone === 'danger'
              ? 'bg-destructive/10 border-destructive/30 text-destructive'
              : 'bg-primary/10 border-primary/30 text-primary')
          : 'bg-muted text-muted-foreground border-transparent';
        return (
          <div key={i} className="flex items-center gap-1.5">
            <div className={`flex items-center gap-1 px-2 py-0.5 rounded-full border text-xs ${toneClass}`}>
              <Icon className="h-3 w-3" />
              <span>{p.label}</span>
              {p.done && p.date && (
                <span className="opacity-70">· {format(new Date(p.date), 'd MMM yyyy', { locale: es })}</span>
              )}
            </div>
            {i < pasos.length - 1 && <div className="w-3 h-px bg-border" />}
          </div>
        );
      })}
    </div>
  );
}

interface Props {
  clienteId: string;
}

export function ContratosClienteTab({ clienteId }: Props) {
  const { perfil, empresa } = useAuth();
  const { clientes, sistemas, sedes } = useSecurityContext();
  const {
    contratos, loading, reload, descargarPdf, verPdf, compartirPdf,
    eliminarContrato, actualizarContrato, subirPdf,
    generarLinkFirma, subirContratoFirmado,
  } = useContratosMantenimiento(clienteId);
  const [dlgOpen, setDlgOpen] = useState(false);
  const [aBorrar, setABorrar] = useState<ContratoMantenimiento | null>(null);
  const [regenerandoId, setRegenerandoId] = useState<string | null>(null);
  const [firmaDlg, setFirmaDlg] = useState<{ contrato: ContratoMantenimiento; email: string; url: string | null } | null>(null);
  const [enviando, setEnviando] = useState(false);
  const [enviandoCorreo, setEnviandoCorreo] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [contratoSubirFirmado, setContratoSubirFirmado] = useState<ContratoMantenimiento | null>(null);
  const [subiendoFirmado, setSubiendoFirmado] = useState(false);
  const [rescindirDlg, setRescindirDlg] = useState<{ contrato: ContratoMantenimiento; fecha: string } | null>(null);
  const [mostrarArchivados, setMostrarArchivados] = useState(false);

  const cliente = clientes.find(c => c.id === clienteId);
  const contratosVisibles = contratos.filter(c => mostrarArchivados || !c.archivado);
  const archivadosCount = contratos.filter(c => c.archivado).length;

  const regenerarPdf = async (c: ContratoMantenimiento, opts?: { forzarDefinitivo?: boolean }) => {
    if (!cliente) {
      toast.error('Cliente no disponible');
      return;
    }
    setRegenerandoId(c.id);

    try {
      const sistemasInfo = c.sistemas_ids.map(id => {
        const s = sistemas.find(x => x.id === id);
        const sede = s?.sede_id ? sedes.find(se => se.id === s.sede_id) : null;
        const direccionPartes = sede ? [sede.direccion, sede.codigo_postal, sede.poblacion, sede.provincia].filter(Boolean).join(', ') : '';
        return {
          tipo: s?.tipo || '',
          tipoLabel: s ? getSystemTypeLabel(s.tipo) : '',
          sedeNombre: sede?.nombre || undefined,
          sedeDireccion: direccionPartes || null,
        };
      });
      const hayIntrusion = c.sistemas_ids.some(id => {
        const s = sistemas.find(x => x.id === id);
        return s?.tipo === 'intrusión';
      });
      const clausulasConCra = hayIntrusion && !c.clausulas.some(cl => cl.titulo === TITULO_CLAUSULA_CRA)
        ? [...c.clausulas, CLAUSULA_CRA]
        : c.clausulas;

      const blob = await generarContratoPdf({
        empresaId: cliente.empresa_id!,
        cliente,
        contrato: {
          numero_formateado: c.numero_formateado,
          tipo: c.tipo as 'seguridad' | 'pci',
          fecha_inicio: c.fecha_inicio,
          fecha_fin: c.fecha_fin,
          duracion_meses: c.duracion_meses,
          renovacion_automatica: c.renovacion_automatica,
          periodicidad_pago: c.periodicidad_pago,
          precio_total: c.precio_total,
          precio_periodo: c.precio_periodo,
          iva_incluido: c.iva_incluido,
          objeto: c.objeto,
          clausulas: clausulasConCra,
          texto_legal: c.texto_legal,
          observaciones: c.observaciones,
          firma_cliente: c.firma_cliente,
          firma_empresa: c.firma_empresa,
          nombre_firmante_cliente: c.nombre_firmante_cliente,
        },
        sistemas: sistemasInfo,
        esBorrador: opts?.forzarDefinitivo ? false : c.estado === 'borrador',
      });


      await subirPdf(c, blob);
      if (!opts?.forzarDefinitivo) toast.success('PDF generado');
    } catch (e: any) {
      toast.error(e.message || 'Error al generar PDF');
      throw e;
    } finally {
      setRegenerandoId(null);
    }

  };

  const esOffice = perfil?.rol === 'admin' || perfil?.rol === 'gestor';

  if (!esOffice) {
    return (
      <Card>
        <CardContent className="py-10 text-center text-muted-foreground">
          No tienes permisos para ver los contratos de este cliente.
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div>
          <h3 className="font-semibold text-lg">Contratos de mantenimiento</h3>
          <p className="text-sm text-muted-foreground">
            Histórico de contratos firmados con este cliente
          </p>
        </div>
        <div className="flex items-center gap-3">
          {archivadosCount > 0 && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Switch
                id="ver-archivados"
                checked={mostrarArchivados}
                onCheckedChange={setMostrarArchivados}
              />
              <Label htmlFor="ver-archivados" className="cursor-pointer">
                Ver archivados ({archivadosCount})
              </Label>
            </div>
          )}
          <Button onClick={() => setDlgOpen(true)}>
            <Plus className="h-4 w-4 mr-2" /> Nuevo contrato
          </Button>
        </div>
      </div>

      {loading ? (
        <p className="text-muted-foreground text-center py-8">Cargando…</p>
      ) : contratosVisibles.length === 0 ? (
        <Card>
          <CardContent className="py-10 text-center text-muted-foreground">
            <FileText className="h-8 w-8 mx-auto mb-2 opacity-50" />
            {contratos.length === 0
              ? 'No hay contratos para este cliente todavía.'
              : 'No hay contratos visibles. Activa "Ver archivados" para mostrarlos.'}
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-3">
          {contratosVisibles.map(c => {
            const est = ESTADO_VARIANT[c.estado] || ESTADO_VARIANT.borrador;
            const TipoIcon = c.tipo === 'seguridad' ? Shield : Flame;
            const esRescindido = c.estado === 'rescindido';
            const esFinalizado = esRescindido || c.estado === 'vencido';
            return (
              <Card key={c.id} className={`hover:shadow-md transition-shadow ${c.archivado ? 'opacity-70' : ''}`}>
                <CardContent className="p-4">
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div className="flex items-start gap-3 min-w-0 flex-1">
                      <div className={`rounded-md p-2 ${c.tipo === 'seguridad' ? 'bg-blue-500/10 text-blue-600' : 'bg-orange-500/10 text-orange-600'}`}>
                        <TipoIcon className="h-5 w-5" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-mono font-semibold">{c.numero_formateado}</span>
                          <Badge variant="outline" className="text-xs">
                            {c.tipo === 'seguridad' ? 'Seguridad' : 'PCI'}
                          </Badge>
                          {(c.estado === 'vencido' || c.estado === 'rescindido') && (
                            <Badge variant={est.variant}>{est.label}</Badge>
                          )}
                          {c.archivado && (
                            <Badge variant="secondary" className="text-xs">Archivado</Badge>
                          )}
                        </div>
                        <ContratoEstadoTimeline c={c} />
                        <div className="text-sm text-muted-foreground mt-2">
                          {format(new Date(c.fecha_inicio), 'd MMM yyyy', { locale: es })}
                          {' → '}
                          {format(new Date(c.fecha_fin), 'd MMM yyyy', { locale: es })}
                          {c.renovacion_automatica && ' · Renovación tácita'}
                        </div>
                        <div className="text-sm mt-1">
                          <span className="font-semibold">{c.precio_total.toFixed(2)} €/año</span>
                          <span className="text-muted-foreground"> · {c.precio_periodo.toFixed(2)} € / {c.periodicidad_pago}</span>
                          {!c.iva_incluido && <span className="text-muted-foreground"> (+ IVA)</span>}
                        </div>
                        <div className="text-xs text-muted-foreground mt-1">
                          {c.sistemas_ids.length} sistema(s) incluido(s)
                        </div>
                      </div>
                    </div>
                    <div className="flex gap-1 flex-wrap">
                      <Button size="sm" variant="outline" onClick={() => verPdf(c)}>
                        <Eye className="h-3.5 w-3.5 mr-1" /> Ver
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => descargarPdf(c)}>
                        <Download className="h-3.5 w-3.5 mr-1" /> PDF
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => compartirPdf(c)}>
                        <Share2 className="h-3.5 w-3.5 mr-1" /> Compartir
                      </Button>
                      {c.estado === 'borrador' && !esFinalizado && (
                        <Button
                          size="sm"
                          variant="default"
                          disabled={regenerandoId === c.id}
                          onClick={async () => {
                            setRegenerandoId(c.id);
                            try {
                              await regenerarPdf(c, { forzarDefinitivo: true });
                              await actualizarContrato(c.id, { estado: 'pendiente_firma' } as any);
                              toast.success('Contrato convertido en definitivo. Ya puedes enviarlo a firmar.');
                            } catch (e: any) {
                              toast.error(e?.message || 'No se pudo convertir');
                            } finally {
                              setRegenerandoId(null);
                            }
                          }}
                          title="Quita la marca de borrador y habilita el envío a firma"
                        >
                          <FileCheck2 className="h-3.5 w-3.5 mr-1" />
                          {regenerandoId === c.id ? 'Generando…' : 'Convertir en definitivo'}
                        </Button>
                      )}
                      {c.estado !== 'borrador' && !esFinalizado && !c.fecha_firma && !c.firma_cliente && (
                        <>
                          <Button
                            size="sm"
                            variant="default"
                            onClick={() => setFirmaDlg({ contrato: c, email: c.firma_email_destinatario || cliente?.email || '', url: null })}
                            title="Enviar enlace de firma online al cliente"
                          >
                            <Send className="h-3.5 w-3.5 mr-1" /> Enviar a firmar
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => setContratoSubirFirmado(c)}
                            title="Subir el PDF firmado escaneado"
                          >
                            <Upload className="h-3.5 w-3.5 mr-1" /> Subir firmado
                          </Button>
                        </>
                      )}

                      {!esRescindido && c.estado !== 'vencido' && (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => setRescindirDlg({ contrato: c, fecha: new Date().toISOString().slice(0, 10) })}
                          title="Marcar el contrato como rescindido"
                        >
                          <Ban className="h-3.5 w-3.5 mr-1" /> Rescindir
                        </Button>
                      )}
                      {esRescindido && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={async () => {
                            try {
                              await actualizarContrato(c.id, {
                                estado: c.fecha_firma ? 'vigente' : 'borrador',
                                fecha_rescision: null,
                              } as any);
                              toast.success('Rescisión deshecha');
                            } catch (e: any) {
                              toast.error(e.message);
                            }
                          }}
                          title="Deshacer la rescisión y reactivar el contrato"
                        >
                          <RotateCcw className="h-3.5 w-3.5 mr-1" /> Reactivar
                        </Button>
                      )}
                      {esFinalizado && (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={async () => {
                            try {
                              await actualizarContrato(c.id, { archivado: !c.archivado } as any);
                              toast.success(c.archivado ? 'Contrato desarchivado' : 'Contrato archivado');
                            } catch (e: any) {
                              toast.error(e.message);
                            }
                          }}
                          title={c.archivado ? 'Quitar del archivo' : 'Archivar este contrato'}
                        >
                          {c.archivado
                            ? <><ArchiveRestore className="h-3.5 w-3.5 mr-1" /> Desarchivar</>
                            : <><Archive className="h-3.5 w-3.5 mr-1" /> Archivar</>}
                        </Button>
                      )}
                      <Button size="sm" variant="ghost" onClick={() => setABorrar(c)}>
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      <ContratoFormDialog
        open={dlgOpen}
        onOpenChange={setDlgOpen}
        clienteId={clienteId}
        onCreated={reload}
      />

      <AlertDialog open={!!aBorrar} onOpenChange={(o) => !o && setABorrar(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminar contrato</AlertDialogTitle>
            <AlertDialogDescription>
              Se eliminará el contrato {aBorrar?.numero_formateado} y su PDF. Esta acción no se puede deshacer.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={async () => {
                if (!aBorrar) return;
                try {
                  await eliminarContrato(aBorrar);
                  toast.success('Contrato eliminado');
                  setABorrar(null);
                } catch (e: any) {
                  toast.error(e.message);
                }
              }}
            >
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Diálogo: Enviar enlace de firma */}
      <Dialog open={!!firmaDlg} onOpenChange={(o) => !o && setFirmaDlg(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Enviar contrato a firmar</DialogTitle>
            <DialogDescription>
              Genera un enlace único para que el cliente firme el contrato {firmaDlg?.contrato.numero_formateado} desde su móvil u ordenador. El contrato pasará a "vigente" automáticamente cuando firme.
            </DialogDescription>
          </DialogHeader>
          {firmaDlg && (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="firma-email">Email del cliente (opcional)</Label>
                <Input
                  id="firma-email"
                  type="email"
                  value={firmaDlg.email}
                  onChange={(e) => setFirmaDlg({ ...firmaDlg, email: e.target.value })}
                  placeholder="cliente@empresa.com"
                />
              </div>
              {firmaDlg.url && (
                <div className="space-y-2">
                  <Label>Enlace de firma</Label>
                  <div className="flex gap-2">
                    <Input readOnly value={firmaDlg.url} className="font-mono text-xs" />
                    <Button
                      type="button"
                      variant="outline"
                      size="icon"
                      onClick={async () => {
                        await navigator.clipboard.writeText(firmaDlg.url!);
                        toast.success('Enlace copiado al portapapeles');
                      }}
                    >
                      <Copy className="h-4 w-4" />
                    </Button>
                  </div>
                  <p className="text-xs text-muted-foreground">Válido durante 30 días. Envíalo por WhatsApp, email o el canal que prefieras.</p>
                </div>
              )}
            </div>
          )}
          <DialogFooter className="flex-wrap gap-2">
            <Button variant="ghost" onClick={() => setFirmaDlg(null)}>Cerrar</Button>
            {firmaDlg?.url ? (
              <>
                <Button
                  variant="outline"
                  onClick={() => {
                    if (!firmaDlg) return;
                    const c = firmaDlg.contrato;
                    const subject = encodeURIComponent(`Firma de contrato ${c.numero_formateado}`);
                    const body = encodeURIComponent(
                      `Hola,\n\nAdjunto el enlace para firmar el contrato de mantenimiento ${c.numero_formateado}.\n\nFirmar aquí: ${firmaDlg.url}\n\nGracias.`,
                    );
                    window.location.href = `mailto:${firmaDlg.email || ''}?subject=${subject}&body=${body}`;
                  }}
                >
                  <Mail className="h-4 w-4 mr-2" /> Abrir email
                </Button>
                <Button
                  variant="outline"
                  disabled={enviandoCorreo || !firmaDlg.email}
                  onClick={async () => {
                    if (!firmaDlg?.url || !firmaDlg.email) {
                      toast.error('Indica un email del cliente');
                      return;
                    }
                    setEnviandoCorreo(true);
                    try {
                      const portalUrl = `${window.location.origin}/portal/login`;
                      const { error } = await supabase.functions.invoke('send-email', {
                        body: {
                          to: firmaDlg.email,
                          subject: `Firma pendiente: contrato ${firmaDlg.contrato.numero_formateado}`,
                          template: 'firma-documento',
                          empresa_id: perfil?.empresa_id,
                          data: {
                            nombre: cliente?.nombre || '',
                            empresa_nombre: empresa?.nombre || '',
                            tipo: 'contrato',
                            numero: firmaDlg.contrato.numero_formateado,
                            url: firmaDlg.url,
                            portal_url: portalUrl,
                          },
                        },
                      });
                      if (error) throw error;
                      toast.success('Correo enviado al cliente');
                    } catch (e: any) {
                      toast.error(e?.message || 'No se pudo enviar el correo');
                    } finally {
                      setEnviandoCorreo(false);
                    }
                  }}
                >
                  <Send className="h-4 w-4 mr-2" /> {enviandoCorreo ? 'Enviando…' : 'Enviar por correo'}
                </Button>
                <Button
                  onClick={async () => {
                    await navigator.clipboard.writeText(firmaDlg.url!);
                    toast.success('Enlace copiado');
                  }}
                >
                  <Copy className="h-4 w-4 mr-2" /> Copiar enlace
                </Button>
              </>
            ) : (
              <Button
                disabled={enviando}
                onClick={async () => {
                  if (!firmaDlg) return;
                  setEnviando(true);
                  try {
                    const res = await generarLinkFirma(firmaDlg.contrato, { email: firmaDlg.email || null });
                    setFirmaDlg({ ...firmaDlg, url: res.url });
                    toast.success('Enlace de firma generado');
                  } catch (e: any) {
                    toast.error(e?.message || 'No se pudo generar el enlace');
                  } finally {
                    setEnviando(false);
                  }
                }}
              >
                <Send className="h-4 w-4 mr-2" /> {enviando ? 'Generando…' : 'Generar enlace'}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Diálogo: Subir contrato firmado */}
      <Dialog open={!!contratoSubirFirmado} onOpenChange={(o) => !o && setContratoSubirFirmado(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Subir contrato firmado</DialogTitle>
            <DialogDescription>
              Sube el PDF firmado por el cliente. Reemplazará el PDF actual y el contrato pasará a "vigente".
            </DialogDescription>
          </DialogHeader>
          <input
            ref={fileInputRef}
            type="file"
            accept="application/pdf,image/*"
            className="hidden"
            onChange={async (e) => {
              const file = e.target.files?.[0];
              if (!file || !contratoSubirFirmado) return;
              setSubiendoFirmado(true);
              try {
                await subirContratoFirmado(contratoSubirFirmado, file);
                toast.success('Contrato firmado guardado y marcado como vigente');
                setContratoSubirFirmado(null);
              } catch (err: any) {
                toast.error(err?.message || 'Error al subir el archivo');
              } finally {
                setSubiendoFirmado(false);
                if (fileInputRef.current) fileInputRef.current.value = '';
              }
            }}
          />
          <DialogFooter>
            <Button variant="ghost" onClick={() => setContratoSubirFirmado(null)}>Cancelar</Button>
            <Button
              disabled={subiendoFirmado}
              onClick={() => fileInputRef.current?.click()}
            >
              <Upload className="h-4 w-4 mr-2" /> {subiendoFirmado ? 'Subiendo…' : 'Seleccionar archivo'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Diálogo: Rescindir contrato */}
      <Dialog open={!!rescindirDlg} onOpenChange={(o) => !o && setRescindirDlg(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Rescindir contrato</DialogTitle>
            <DialogDescription>
              Marca el contrato {rescindirDlg?.contrato.numero_formateado} como rescindido. Podrás deshacerlo más tarde si lo necesitas.
            </DialogDescription>
          </DialogHeader>
          {rescindirDlg && (
            <div className="space-y-2">
              <Label htmlFor="rescision-fecha">Fecha de rescisión</Label>
              <Input
                id="rescision-fecha"
                type="date"
                value={rescindirDlg.fecha}
                onChange={(e) => setRescindirDlg({ ...rescindirDlg, fecha: e.target.value })}
              />
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setRescindirDlg(null)}>Cancelar</Button>
            <Button
              variant="destructive"
              onClick={async () => {
                if (!rescindirDlg) return;
                try {
                  await actualizarContrato(rescindirDlg.contrato.id, {
                    estado: 'rescindido',
                    fecha_rescision: new Date(rescindirDlg.fecha).toISOString(),
                  } as any);
                  toast.success('Contrato rescindido');
                  setRescindirDlg(null);
                } catch (e: any) {
                  toast.error(e.message);
                }
              }}
            >
              <Ban className="h-4 w-4 mr-2" /> Confirmar rescisión
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
